import * as hls from 'hls-parser';

import logger              from '../../../Shared/Util/logger';
import arrayBufferToString from '../../Shared/Util/arrayBufferToString';
import ManifestError       from '../Constants/ManifestError';

const manifestCache: {[key: string]: string} = {};

/**
 * Uses the performance resources API to get a list of the
 * last sucessful manifest request and returns its URL.
 */

const getLastManifestUrl = (perf = window.performance): string => {
    if (!perf || typeof perf.getEntriesByType !== 'function') return '';

    const manifests = perf.getEntriesByType('resource')
        .filter((resource: PerformanceResourceTiming) =>
            resource.initiatorType === 'video' && resource.name.includes('.m3u8')
        );

    const lastManifest = manifests.length > 0 ? manifests[manifests.length - 1] : null;

    return lastManifest ? lastManifest.name : '';
};

/**
 * Checks if the provided variant URI matches the provided
 * manifest URL.
 */

const testVariantUriAgainstLastManifestUrl = (variantManifestUri: string, lastManifestUrl: string): boolean => {
    // Strip off any query strings, as may not be present on both

    variantManifestUri = variantManifestUri.split('?')[0];
    lastManifestUrl = lastManifestUrl.split('?')[0];

    // Create a RegExp from the URL asserting URI position at end of URL

    const re = new RegExp(variantManifestUri + '$');

    return Boolean(lastManifestUrl.match(re));
};

const extractCurrentBitrateFromManifestContent = (
    manifestObj: any,
    injectedGetLastManifestUrl = getLastManifestUrl
): number => {
    const variants: any[] = manifestObj.variants;
    const lastManifestUrl = injectedGetLastManifestUrl();

    if (!lastManifestUrl) return 0;

    const activeVariant = variants.find(variant => testVariantUriAgainstLastManifestUrl(variant.uri, lastManifestUrl));

    return activeVariant ? activeVariant.bandwidth : 0;
};

const getCurrentBitrateFromManifest = async (
    manifestUrl: string,
    fetch = window.fetch
): Promise<number> => {
    // Read manifest from cache

    let manifestObj: any = manifestCache[manifestUrl];

    // If manifest has not been previously requested and cached, parse it
    // and put it in the cache.

    if (typeof manifestObj === 'undefined') {
        let response: Response = null;

        try {
            response = await fetch(manifestUrl);
        } catch (err) {
            logger.error(err);
        }

        if (response === null || !response.ok) throw new Error(ManifestError.MANIFEST_REQUEST_FAILED);

        const manifestData = await response.arrayBuffer();

        const manifestContent = arrayBufferToString(manifestData)
            .replace(/#EXT-X-VERSION:[0-4]/mg, '#EXT-X-VERSION:5'); // Allow legacy manifests by faking v5

        try {
            // @ts-ignore
            manifestObj = manifestCache[manifestUrl] = hls.parse(manifestContent);
        } catch (err) {
            logger.error(err);

            throw new Error(ManifestError.MALFORMED_MANIFEST);
        }
    }

    const bitrate = extractCurrentBitrateFromManifestContent(manifestObj);

    return bitrate;
};

export {
    getCurrentBitrateFromManifest as default,
    extractCurrentBitrateFromManifestContent,
    testVariantUriAgainstLastManifestUrl,
    getLastManifestUrl,
    manifestCache
};